import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/energy/LeadForm";
import { ToolShell, NumInput, SelectInput, ErrorBox, ResultGrid, EstimateNote, sle } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

export default function EnergyCompare() {
  const [form, setForm] = useState({ capacity_kw: 3, fuel_type: "petrol", litres_per_day: "", fuel_price_sle: "", hours_per_day: "", days_per_month: 30, maintenance_monthly_sle: "", purchase_cost_sle: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showLead, setShowLead] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const compute = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      const { data } = await api.post("/energy/compare", payload);
      setResult(data.result);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Generator vs Solar Cost Calculator — BNM" description="Compare your generator's fuel and maintenance costs against an estimated solar + battery system, in Leones." />
      <ToolShell
        testid="compare-tool"
        eyebrow="Generator vs Solar"
        title="What does your generator really cost?"
        lead="Enter how you use your generator. We'll estimate its true long-term cost and compare it with an estimated solar + battery system. All figures are estimates — actual savings depend on system design, usage, weather, maintenance, fuel prices and equipment costs."
      >
        <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <NumInput label="Generator capacity" suffix="kVA" min={0.5} max={500} testid="cmp-capacity" value={form.capacity_kw} onChange={set("capacity_kw")} />
            <SelectInput label="Fuel type" testid="cmp-fuel-type" value={form.fuel_type} onChange={set("fuel_type")}
              options={[{ value: "petrol", label: "Petrol" }, { value: "diesel", label: "Diesel" }]} />
            <NumInput label="Fuel used per day" suffix="litres" min={0} testid="cmp-litres" value={form.litres_per_day} onChange={set("litres_per_day")} />
            <NumInput label="Fuel price (leave blank for BNM default)" suffix="SLE/litre" min={0} testid="cmp-fuel-price" value={form.fuel_price_sle} onChange={set("fuel_price_sle")} />
            <NumInput label="Running hours per day" suffix="h" min={0} max={24} testid="cmp-hours" value={form.hours_per_day} onChange={set("hours_per_day")} />
            <NumInput label="Operating days per month" min={1} max={31} step={1} testid="cmp-days" value={form.days_per_month} onChange={set("days_per_month")} />
            <NumInput label="Maintenance per month" suffix="SLE" min={0} testid="cmp-maint" value={form.maintenance_monthly_sle} onChange={set("maintenance_monthly_sle")} />
            <NumInput label="Generator purchase cost (optional)" suffix="SLE" min={0} testid="cmp-purchase" value={form.purchase_cost_sle} onChange={set("purchase_cost_sle")} />
          </div>
          {error && <div className="mt-5"><ErrorBox msg={error} /></div>}
          <button onClick={compute} disabled={busy} data-testid="cmp-compute"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
            {busy ? "Calculating…" : "Compare Costs"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {result && (
          <div className="mt-10 space-y-8" data-testid="compare-results">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">Your Estimated Energy Cost (generator)</h2>
              <div className="mt-4">
                <ResultGrid items={[
                  { label: "Daily fuel", value: sle(result.generator.daily_fuel_sle) },
                  { label: "Monthly (fuel + maintenance)", value: sle(result.generator.monthly_sle) },
                  { label: "Annual", value: sle(result.generator.annual_sle) },
                  { label: "5-year total", value: sle(result.generator.total_5yr_sle), hint: "Including purchase cost if entered" },
                ]} />
              </div>
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">Estimated Solar + Battery Equivalent</h2>
              <div className="mt-4">
                <ResultGrid items={[
                  { label: "Solar array", value: `${result.solar.installed_kw} kW`, hint: `${result.solar.panels} × ${result.solar.panel_w} W panels` },
                  { label: "Battery", value: `${result.solar.battery_kwh} kWh` },
                  { label: "Inverter", value: `${result.solar.inverter_kw} kW` },
                  { label: "System cost (est.)", value: sle(result.solar.system_cost_sle), hint: result.solar.system_cost_usd ? `≈ $${result.solar.system_cost_usd.toLocaleString()}` : "" },
                ]} />
              </div>
            </div>
            <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-6 sm:p-8" data-testid="compare-verdict">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700">Potential Long-Term Cost Difference (5 years)</p>
              <p className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
                {result.difference_5yr_sle >= 0 ? `${sle(result.difference_5yr_sle)} potential saving` : `${sle(Math.abs(result.difference_5yr_sle))} — solar still ahead on cost at these inputs`}
              </p>
              {result.payback_months && (
                <p className="mt-2 text-sm text-slate-600">Estimated payback: ~{result.payback_months} months of generator running costs.</p>
              )}
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Estimated figures only. Actual savings depend on system design, usage patterns, weather, maintenance, fuel prices and equipment costs.
              </p>
            </div>
            <EstimateNote />
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowLead(true)} data-testid="compare-cta"
                className="rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                Get a BNM Energy Assessment
              </button>
            </div>
            {showLead && <LeadForm type="assessment" title="Get a BNM Energy Assessment" context={{ generator_5yr_sle: result.generator.total_5yr_sle, solar_cost_sle: result.solar.system_cost_sle }} defaultMessage="I compared my generator costs with solar and would like an assessment." />}
          </div>
        )}
      </ToolShell>
    </>
  );
}
